/**
 * One question: a single line when closed, a full editor when open.
 *
 * The closed row carries everything needed to scan a paper - number, stem, type,
 * marks, and whether it needs attention - in 36px. Cards would show six questions
 * on a screen where this shows twenty.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { MatchingPair, OptionIn, Question } from '../../lib/api/client'
import { Button, ConfirmButton, IconButton, Input, Select, Checkbox } from '../../components/ui'
import { Icon, questionIcon } from '../../components/icons'
import { OptionalSettings, SchemaFields } from './SchemaFields'
import { OptionsEditor } from './OptionsEditor'
import { MatchingEditor } from './MatchingEditor'
import { ActivityEditor } from './ActivityEditors'

const CUSTOM_EDITOR_FALLBACKS: Record<string, string> = {
  matching: 'matching',
  missing_number: 'missing_number',
  count_pictures: 'count_pictures',
  compare_sizes: 'compare_sizes',
  shape_properties: 'circle_correct',
  write_in_box: 'write_in_box',
  tick_cross_pictures: 'tick_cross_pictures',
}

export function QuestionRow({
  worksheetId,
  question,
  index,
  paperNumber,
  total,
  hasPassageChildren,
  problems,
  marksEnabled,
  editable,
  open,
  onToggle,
  variant = 'combined',
  onSaved,
  onSaving,
  onFailed,
}: {
  worksheetId: string
  question: Question
  index: number
  /** Position in the whole paper - numbering runs on across sections. */
  paperNumber: string
  total: number
  hasPassageChildren: boolean
  problems: string[]
  marksEnabled: boolean
  editable: boolean
  open: boolean
  onToggle: () => void
  variant?: 'combined' | 'outline' | 'editor'
  onSaved: () => void
  onSaving: () => void
  onFailed: (error: Error) => void
}) {
  const types = useQuery({ queryKey: ['question-types'], queryFn: api.questionTypes })
  const type = types.data?.find((entry) => entry.type_key === question.type)
  // The stable question type is a safe fallback while type metadata is loading
  // or when a browser still holds a response cached before editor_key existed.
  // Without it, rich activities briefly (or permanently) fall through to the
  // multiple-choice editor and save their rows in the wrong shape.
  const editorKey =
    type?.editor_key && type.editor_key !== 'schema'
      ? type.editor_key
      : CUSTOM_EDITOR_FALLBACKS[question.type]
        ? CUSTOM_EDITOR_FALLBACKS[question.type]
        : 'schema'

  const run = (work: Promise<unknown>) => {
    onSaving()
    return work.then(onSaved).catch(onFailed)
  }

  const patchPayload = (patch: Record<string, unknown>) =>
    run(
      api.updateQuestion(worksheetId, question.id, {
        payload: { ...question.payload, ...patch },
      }),
    )

  const setOptions = (options: OptionIn[]) =>
    run(api.setOptions(worksheetId, question.id, options))

  const setPairs = (pairs: MatchingPair[]) =>
    run(api.setMatchingPairs(worksheetId, question.id, pairs))

  const numbered = type?.numbered ?? true
  const marksAllowed = type?.marks_allowed ?? numbered
  const showSummary = variant !== 'editor'
  const showBody = variant === 'editor' || (variant === 'combined' && open)

  // Each type keeps its text under its own field name; the first non-empty one
  // is what a teacher recognises when scanning the list.
  const stem =
    [
      question.payload.stem,
      question.payload.prompt,
      question.payload.question,
      question.payload.sentence,
      question.payload.statement,
      question.payload.title,
      question.payload.text,
    ]
      .map((value) => String(value ?? '').trim())
      .find(Boolean) || (numbered ? 'Untitled question' : 'Empty passage')

  return (
    <div
      className={[
        'tc-question',
        open ? 'is-open' : '',
        problems.length ? 'has-problem' : '',
        question.passage_id ? 'is-comprehension' : '',
        variant === 'outline' ? 'is-outline' : '',
        variant === 'editor' ? 'is-editor' : '',
      ].filter(Boolean).join(' ')}
    >
      {showSummary ? (
        <button
          type="button"
          className="tc-question-row"
          aria-expanded={variant === 'combined' ? open : undefined}
          aria-current={variant === 'outline' && open ? 'true' : undefined}
          onClick={onToggle}
        >
          <span className="tc-q-index">{numbered ? paperNumber : '—'}</span>
          <span className="tc-q-icon">{questionIcon(question.type)}</span>
          <span className="tc-q-summary">{stem}</span>
          <span className="tc-q-type">
            {question.passage_id ? `Passage · ${question.type_name}` : question.type_name}
          </span>
          {marksEnabled && question.marks != null && (
            <span className="tc-q-marks">{question.marks}</span>
          )}
          {problems.length > 0 && (
            <span className="tc-q-problem" title={problems.join(' ')}>
              <Icon.warning size={14} />
              <span>{problems[0]}</span>
            </span>
          )}
        </button>
      ) : (
        <header className="tc-question-editor-head">
          <span className="tc-q-icon">{questionIcon(question.type, 18)}</span>
          <span>
            <span className="tc-question-editor-kicker">
              {numbered ? `Question ${paperNumber}` : 'Paper content'}
            </span>
            <strong>{question.type_name}</strong>
          </span>
          {marksEnabled && question.marks != null && (
            <span className="tc-q-marks">{question.marks} marks</span>
          )}
        </header>
      )}

      {showBody && (
        <div className="tc-question-body">
          {problems.map((problem, position) => (
            <p key={position} className="tc-problem" role="status">
              <Icon.warning size={14} /> {problem}
            </p>
          ))}

          {!editable ? (
            <p className="tc-field-hint">This paper is locked, so it cannot be edited right now.</p>
          ) : (
            <>
              {type && (
                <SchemaFields
                  schema={type.fields_schema}
                  value={question.payload}
                  onCommit={patchPayload}
                />
              )}

              {/* Matching needs two columns, which the generated editor cannot
                  express - so the plugin asks for its own. */}
              {editorKey === 'matching' ? (
                <MatchingEditor options={question.options} onChange={setPairs} />
              ) : editorKey !== 'schema' ? (
                <ActivityEditor
                  editorKey={editorKey}
                  mode={question.payload.mode}
                  shuffleSizes={question.payload.shuffle_sizes}
                  options={question.options}
                  onChange={setOptions}
                />
              ) : (
                question.uses_options && (
                  <OptionsEditor
                    options={question.options}
                    allowMultiple={Boolean(question.payload.allow_multiple)}
                    onChange={setOptions}
                  />
                )
              )}

              {marksEnabled && marksAllowed && (
                <Marks
                  marks={question.marks}
                  onChange={(marks) =>
                    run(
                      api.updateQuestion(worksheetId, question.id, {
                        marks,
                        clear_marks: marks == null,
                      }),
                    )
                  }
                />
              )}

              {question.type === 'passage' && (
                <div className="tc-comprehension-add">
                  <div>
                    <div className="tc-field-label">Questions about this passage</div>
                    <div className="tc-field-hint">
                      Add a question here to keep it attached directly beneath the passage.
                    </div>
                  </div>
                  <div className="tc-add-grid">
                    <button
                      type="button"
                      className="tc-add-btn"
                      onClick={() =>
                        run(api.addQuestion(worksheetId, 'multiple_choice', null, question.id))
                      }
                    >
                      {questionIcon('multiple_choice', 15)}
                      Multiple choice
                    </button>
                    <button
                      type="button"
                      className="tc-add-btn"
                      onClick={() =>
                        run(api.addQuestion(worksheetId, 'fill_in_blanks', null, question.id))
                      }
                    >
                      {questionIcon('fill_in_blanks', 15)}
                      Fill in the blanks
                    </button>
                  </div>
                </div>
              )}

              {/* Last thing in the form, below the answers. Page layout is
                  adjusted after the question is written, if at all. */}
              {type && (
                <OptionalSettings
                  schema={type.fields_schema}
                  value={question.payload}
                  onCommit={patchPayload}
                />
              )}

              <div className="tc-question-actions">
                {!question.passage_id && !hasPassageChildren && (
                  <>
                    <IconButton
                      label="Move up"
                      size="sm"
                      disabled={index === 0}
                      onClick={() => run(api.moveQuestion(worksheetId, question.id, index))}
                    >
                      <Icon.up size={15} />
                    </IconButton>
                    <IconButton
                      label="Move down"
                      size="sm"
                      disabled={index === total - 1}
                      onClick={() => run(api.moveQuestion(worksheetId, question.id, index + 2))}
                    >
                      <Icon.down size={15} />
                    </IconButton>

                    {/* Nudging is fine for five questions and miserable for forty. */}
                    {total > 3 && (
                      <Select
                        aria-label="Move to position"
                        value={index + 1}
                        style={{ width: 120 }}
                        onChange={(event) =>
                          run(api.moveQuestion(worksheetId, question.id, Number(event.target.value)))
                        }
                      >
                        {Array.from({ length: total }, (_, position) => (
                          <option key={position} value={position + 1}>
                            Position {position + 1}
                          </option>
                        ))}
                      </Select>
                    )}
                  </>
                )}

                <div className="tc-spacer" />

                <Button
                  size="sm"
                  icon={<Icon.copy size={14} />}
                  onClick={() => run(api.duplicateQuestion(worksheetId, question.id))}
                >
                  Duplicate
                </Button>
                <ConfirmButton
                  label="Delete"
                  message={
                    hasPassageChildren
                      ? 'This passage and all questions attached to it will be removed.'
                      : 'This question will be removed.'
                  }
                  onConfirm={() => run(api.deleteQuestion(worksheetId, question.id))}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Marks are always optional - an exam needs them, a worksheet usually does not
 *  (DR-006). Off means no marks at all, never a zero. */
function Marks({
  marks,
  onChange,
}: {
  marks: number | null | undefined
  onChange: (marks: number | null) => void
}) {
  const enabled = marks != null

  return (
    <div className="tc-marks-row">
      <Checkbox
        label="Give this question marks"
        checked={enabled}
        onChange={(event) => onChange(event.target.checked ? 1 : null)}
      />
      {enabled && (
        <Input
          type="number"
          min={0.5}
          max={100}
          step={0.5}
          value={String(marks)}
          style={{ width: 88 }}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      )}
    </div>
  )
}
